import { FunctionComponent, ReactElement, useCallback } from "react";

import { styled } from "@mui/material";

import { Info } from "@mui/icons-material";

import { GridRowId } from "@mui/x-data-grid";

import { Paper } from "../../../components/common";
import { FreebieRuleType } from "../../../utils/enums";

import { FreebieVariants } from "./FreebieVariants";
import { TriggerVariants } from "./TriggerVariants";
import {
    FreebieRuleSection,
    IFreebieRuleForm,
    SectionTitle,
    StyledTooltip,
} from "./common";

const Title = styled("h1")`
    font-family: Poppins;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0%;
`;

const VariantContainer = styled("div")`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
`;

const ProductContainer = styled("div")`
    display: flex;
    flex-direction: column;
`;

export interface IVariantsSectionProps {
    values: IFreebieRuleForm;
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean,
    ) => Promise<void> | Promise<any>;
}

export const VariantsSection: FunctionComponent<IVariantsSectionProps> = (
    props: IVariantsSectionProps,
): ReactElement => {
    const { values, setFieldValue } = props;

    const handleTriggerVariantIdsChange = useCallback(
        (newValue: GridRowId[] | undefined) => {
            setFieldValue("triggerVariantIds", newValue);
        },
        [setFieldValue],
    );

    const handleFreebieVariantIdsChange = useCallback(
        (newValue: GridRowId[] | undefined) => {
            setFieldValue("freebieVariantIds", newValue);
        },
        [setFieldValue],
    );

    return (
        <Paper>
            <FreebieRuleSection>
                <SectionTitle>Products</SectionTitle>
                <ProductContainer>
                    {values.type === FreebieRuleType.ELIGIBILITY && (
                        <>
                            <VariantContainer>
                                <Title>Trigger Variants</Title>
                                <StyledTooltip
                                    enterTouchDelay={0}
                                    placement="top"
                                    title="If any of these product variants is in the cart, the freebie is granted."
                                >
                                    <Info
                                        htmlColor="#626262"
                                        fontSize="small"
                                    />
                                </StyledTooltip>
                            </VariantContainer>
                            <TriggerVariants
                                triggerVariantIds={values.triggerVariantIds}
                                onChange={handleTriggerVariantIdsChange}
                            />
                        </>
                    )}

                    <VariantContainer>
                        <Title>Freebie Variants</Title>
                        <StyledTooltip
                            enterTouchDelay={0}
                            placement="top"
                            title="Product variant(s) granted for free when this rule triggers."
                        >
                            <Info htmlColor="#626262" fontSize="small" />
                        </StyledTooltip>
                    </VariantContainer>
                    <FreebieVariants
                        freebieVariantIds={values.freebieVariantIds}
                        onChange={handleFreebieVariantIdsChange}
                    />
                </ProductContainer>
            </FreebieRuleSection>
        </Paper>
    );
};
